// ── SALES TAX — FILE & RECORD modal (2026-10-15; v2 2026-10-16) ─────────────
// Three steps for one state + one period: (1) what's due, with the items
// behind the figure, (2) file on the state portal, (3) record the filing
// with its confirmation PDF. Saving writes the filing, stamps every ticked
// item (tax dollars frozen at save), and stores the PDF — all as rows.
//
// v2 (design handoff CHANGES.md, 2026-10-16): the catch-up / multi-period
// mode is GONE. On delivery basis Wyoming's first return is simply one
// large October row — the ordinary single-period modal handles it. The
// header gains a "first return" suffix when the period is the first since
// the state's registered_from, and the subtitle / Step 1 note say
// "delivery basis" when the state buckets by delivery into the state.
//
// Built from the Claude Design handoff (design_handoff_sales_tax_filing/
// README.md + Sales Tax Filing.dc.html). Geometry, copy and tokens are
// reproduced as specified; where the HTML and README disagree, the HTML
// wins for visuals and the README for behavior.
//
// Hard rule (Cimon, 2026-10-15): payments are stored facts. Nothing on an
// item is ever recomputed to show what was paid — surfaces read the
// sales_tax_filing_items row this modal writes.
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useAppContext } from './AppContext';
import { CurrencyInput } from './ui';
import {
  fmtMoney, salesTaxFilingText, salesTaxReportCsv, salesTaxFilingsFor, sumSalesTaxFilings,
  salesTaxFilingItemGroups, salesTaxDueDate, salesTaxDueRuleFor, salesTaxDueRuleLabel,
  salesTaxStateRuleFor, salesTaxFirstReturn, salesTaxReturnFormLines,
  salesTaxShortDate, salesTaxRecorderName,
  TAX_CATEGORY_KEYS, TAX_CATEGORY_LABELS,
} from '../utils';
import {
  INK_1, INK_2, INK_3, INK_4, SURFACE, LINE_CONTAINER, LINE_SHELL,
  ZONE_FUCHSIA, ZONE_GREEN, ALERT_RED_TEXT, ALERT_AMBER_TEXT,
  HAIRLINE, HAIRLINE_ROW, HAIRLINE_SHELL, DASHED_DROP, SCRIM, SHADOW_MODAL, TABULAR,
} from '../lib/designTokens';

export var FILING_METHODS = ['ACH', 'Credit card', 'Check', 'Wire'];

var EYEBROW = { fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: INK_3 };
var LABEL   = Object.assign({ display: 'block', marginBottom: 4 }, EYEBROW);
var INPUT   = { width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '0.5px solid ' + LINE_SHELL, borderRadius: 4, fontSize: 12, color: INK_1, outline: 'none', fontFamily: 'inherit', background: SURFACE };
var AMOUNT  = Object.assign({}, INPUT, { padding: '8px 10px', fontSize: 14, fontWeight: 500, textAlign: 'right', fontFeatureSettings: TABULAR });
var FIG_ROW = { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: HAIRLINE_ROW, fontSize: 12 };
var COPY_BTN = { height: 22, padding: '0 8px', borderRadius: 11, border: '0.5px solid ' + LINE_CONTAINER, background: SURFACE, color: '#666', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' };
var GHOST26  = { height: 26, padding: '0 12px', borderRadius: 13, border: '0.5px solid ' + LINE_SHELL, background: SURFACE, color: INK_1, fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' };
var BACK_BTN = { height: 32, padding: '0 14px', borderRadius: 18, border: '0.5px solid ' + LINE_CONTAINER, background: SURFACE, color: INK_1, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' };
var PRIMARY  = { height: 32, padding: '0 16px', borderRadius: 18, border: 'none', background: INK_1, color: '#FFFFFF', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' };

function methodPill(on) {
  return { height: 26, padding: '0 12px', borderRadius: 13, border: on ? '1.5px solid ' + INK_1 : '0.5px solid ' + LINE_SHELL, background: SURFACE, color: on ? INK_1 : INK_3, fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' };
}
function r2(x) { return Math.round((Number(x) || 0) * 100) / 100; }
function plain(n) { return r2(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
function ratePct(rate) { return rate == null ? 'mixed' : (rate ? (rate * 100).toFixed(3) + '%' : '—'); }
function fmtBytes(n) { if (!n && n !== 0) return ''; if (n < 1024) return n + ' B'; if (n < 1024 * 1024) return Math.round(n / 1024) + ' KB'; return (n / (1024 * 1024)).toFixed(1) + ' MB'; }
function todayIso() { return new Date().toISOString().slice(0, 10); }
function mintId() { return (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : (Date.now() + '-' + Math.random()).toString(); }
function catLabels(keys) { return (keys || []).map(function(k) { return TAX_CATEGORY_LABELS[k] || k; }).join(', '); }
function downloadCsv(filename, csvText) {
  var blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
  var url = URL.createObjectURL(blob);
  var a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  window.setTimeout(function() { URL.revokeObjectURL(url); }, 0);
}
function copyText(txt, done) {
  if (!txt) return;
  var fallback = function() {
    try {
      var ta = document.createElement('textarea'); ta.value = txt;
      ta.style.position = 'fixed'; ta.style.opacity = '0'; ta.style.left = '-9999px';
      document.body.appendChild(ta); ta.focus(); ta.select();
      var ok = document.execCommand('copy'); document.body.removeChild(ta);
      if (ok && done) done();
    } catch (e) { /* nothing more to try */ }
  };
  if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(txt).then(function() { if (done) done(); }, fallback);
  else fallback();
}
function emptyFig() {
  var cats = { nonTaxable: {} };
  TAX_CATEGORY_KEYS.forEach(function(k) { cats.nonTaxable[k] = 0; });
  return { grossSales: 0, nonTaxable: 0, taxable: 0, tax: 0, invoiced: 0, cats: cats, taxedCategories: [], jurisdictions: [], rate: null, other: 0 };
}
function deductionRows(fig) {
  var rows = [];
  TAX_CATEGORY_KEYS.forEach(function(k) {
    var v = fig.cats && fig.cats.nonTaxable ? fig.cats.nonTaxable[k] : 0;
    if (v > 0.005) rows.push({ label: TAX_CATEGORY_LABELS[k] || k, amount: v });
  });
  var other = fig.cats ? (fig.cats.other || fig.other || 0) : 0;
  if (other > 0.005) rows.push({ label: 'Other', amount: other });
  return rows;
}

// props: stateCode · stateName · periods [{period, st}] (exactly one) ·
// ret (the RETURN being filed: {key,label,jurisdiction,rate,tax} — 2026-10-16;
// omitted = the state's only / first return) · existing (filing to edit) ·
// grouping · initialStep · onClose
export default function SalesTaxFilingModal(props) {
  var ctx = useAppContext();
  var periods = props.periods || [];
  var single = periods[0] || null;
  var existing = props.existing || null;
  var stateCode = props.stateCode || (single ? single.st.stateCode : '');
  var stateName = props.stateName || (single ? single.st.stateName : stateCode);
  var grouping = props.grouping || 'month';
  var isOwner = !!ctx.isOwner;
  var userEmail = ctx.userEmail || '';
  var recorderName = salesTaxRecorderName(existing ? (existing.recordedBy || userEmail) : userEmail, ctx.firmSettings);

  var refs = { invoices: ctx.invoices || [], items: ctx.items || [], finalInvoiceItems: ctx.finalInvoiceItems || [], projects: ctx.projects || [] };
  var salesTaxFilings = ctx.salesTaxFilings || [];
  var salesTaxFilingItems = ctx.salesTaxFilingItems || [];
  var salesTaxFilingFiles = ctx.salesTaxFilingFiles || [];
  var stateTaxRecords = ctx.stateTaxRecords || [];
  // ── The return (2026-10-16). A multi-return state (Colorado + Town of
  // Vail) files one return at a time; the figures below are THIS return's
  // share of the row. A one-return state's return carries the whole row.
  var returns = (single && single.st.returns) || [];
  var ret = props.ret || (existing ? returns.find(function(r) { return r.key === String(existing.returnKey || '').toUpperCase(); }) : null) || returns[0] || null;
  var multi = returns.length > 1;
  var retTax = ret ? r2(ret.tax) : (single ? r2(single.st.tax) : 0);
  var ratio = single && single.st.tax > 0 ? retTax / single.st.tax : 1;
  var stateRec = salesTaxStateRuleFor(stateTaxRecords, stateCode, ret ? ret.jurisdiction : '');
  var stateRule = salesTaxStateRuleFor(stateTaxRecords, stateCode);   // basis / registration live on the STATE record
  var dueRule = stateRec ? (stateRec.dueRule || '') : salesTaxDueRuleFor(stateTaxRecords, stateCode);
  var deliveryBasis = !!(stateRule && stateRule.taxBasis === 'delivery');
  var firstReturn = !!(single && stateRule && salesTaxFirstReturn(single.period.key, stateRule.registeredFrom, grouping));

  // ── Items behind the period (frozen from the FIN snapshot).
  var groups = useMemo(function() {
    return single ? salesTaxFilingItemGroups(single.st, refs) : [];
  }, [single, ctx.invoices, ctx.items, ctx.finalInvoiceItems, ctx.projects]); // eslint-disable-line react-hooks/exhaustive-deps

  // Items already stamped by ANOTHER filing — never double-stamped.
  var stampedElsewhere = useMemo(function() {
    var m = {};
    salesTaxFilingItems.forEach(function(fi) {
      if (existing && fi.filingId === existing.id) return;
      var f = salesTaxFilings.find(function(x) { return x.id === fi.filingId; });
      m[fi.itemId] = f ? (f.confirmationNo || f.paidDate || 'another filing') : 'another filing';
    });
    return m;
  }, [salesTaxFilingItems, salesTaxFilings, existing]);

  // ── State.
  var ss = useState(props.initialStep || (existing ? 3 : 1)); var step = ss[0]; var setStep = ss[1];
  var tk = useState(function() {
    var m = {};
    // An existing filing with NO stamps at all (its items phase failed —
    // Cimon's first Vail filing, 2026-10-16) ticks like a new one, so
    // pressing Save again stamps the items instead of silently none.
    var existingHasStamps = !!existing && salesTaxFilingItems.some(function(fi) { return fi.filingId === existing.id; });
    groups.forEach(function(g) { g.items.forEach(function(it) {
      if (existingHasStamps) m[it.itemId] = salesTaxFilingItems.some(function(fi) { return fi.filingId === existing.id && fi.itemId === it.itemId; });
      else m[it.itemId] = !stampedElsewhere[it.itemId];
    }); });
    return m;
  }); var ticked = tk[0]; var setTicked = tk[1];
  var owed = single ? Math.max(0, r2(retTax - sumSalesTaxFilings(salesTaxFilingsFor(salesTaxFilings, stateCode, single.period.key, ret ? ret.key : '').filter(function(f) { return !existing || f.id !== existing.id; })))) : 0;
  var dr = useState(function() {
    return existing ? {
      amountPaid: r2(existing.amountPaid), paidDate: existing.paidDate || todayIso(), filedDate: existing.filedDate || todayIso(),
      method: existing.method || '', confirmationNo: existing.confirmationNo || '', notes: existing.notes || '',
    } : {
      amountPaid: owed,
      paidDate: todayIso(), filedDate: todayIso(), method: 'ACH', confirmationNo: '', notes: '',
    };
  }); var draft = dr[0]; var setDraft = dr[1];
  var pf = useState([]); var pendingFiles = pf[0]; var setPendingFiles = pf[1];   // [{localId, file}]
  var rf = useState([]); var removedFileIds = rf[0]; var setRemovedFileIds = rf[1];
  var bz = useState(false); var busy = bz[0]; var setBusy = bz[1];
  var er = useState(''); var err = er[0]; var setErr = er[1];
  var cp = useState(''); var copied = cp[0]; var setCopied = cp[1];
  var rv = useState(false); var reveal = rv[0]; var setReveal = rv[1];
  var dg = useState(false); var dragOver = dg[0]; var setDragOver = dg[1];
  // Pre-minted filing id so a retry upserts the SAME row (phased submit).
  var idRef = useRef('');
  useEffect(function() {
    if (!idRef.current) idRef.current = existing ? existing.id : mintId();
  }, [existing]);
  var fileInputRef = useRef(null);

  var existingFiles = existing ? salesTaxFilingFiles.filter(function(f) { return f.filingId === existing.id && removedFileIds.indexOf(f.id) < 0; }) : [];
  var existingStamps = existing ? salesTaxFilingItems.filter(function(fi) { return fi.filingId === existing.id; }) : [];

  function setD(k, v) { setDraft(function(prev) { return Object.assign({}, prev, { [k]: v }); }); }
  function flash(key) { setCopied(key); window.setTimeout(function() { setCopied(''); }, 1600); }

  // ── Derived figures.
  var fig = single ? Object.assign({}, single.st, { tax: retTax, rate: ret && multi ? ret.rate : single.st.rate }) : emptyFig();
  // Each item's share for THIS return: its row tax × the return's share
  // of the state's tax. itemTotal keeps the whole figure for the copy
  // "$490.00 of $940.00". Residue (cents) lands on the first item so the
  // ticked shares always sum to the return's figure.
  var allItems = [];
  groups.forEach(function(g) { g.items.forEach(function(it) { allItems.push(Object.assign({ invoiceRef: g.invoiceRef, invoiceId: g.invoiceId }, it, { itemTotal: it.tax, tax: r2(it.tax * ratio) })); }); });
  if (multi && allItems.length) {
    var placed = r2(allItems.reduce(function(s, it) { return s + it.tax; }, 0));
    var shouldBe = r2(allItems.reduce(function(s, it) { return s + it.itemTotal; }, 0) * ratio);
    if (Math.abs(shouldBe - placed) >= 0.005) allItems[0].tax = r2(allItems[0].tax + (shouldBe - placed));
  }
  var tickedItems = allItems.filter(function(it) { return ticked[it.itemId]; });
  var untickedItems = allItems.filter(function(it) { return !ticked[it.itemId]; });
  var untickedTax = r2(untickedItems.reduce(function(s, it) { return s + it.tax; }, 0));
  var finCount = (function() { var seen = {}; tickedItems.forEach(function(it) { seen[it.invoiceId] = true; }); return Object.keys(seen).length; })();
  var dueDate = single ? salesTaxDueDate(single.period.key, dueRule) : '';

  // ── Title / subtitle.
  var projectsText = (function() { var seen = {}; (single && single.st.projects || []).forEach(function(pr) { seen[pr.project] = true; }); return Object.keys(seen).sort().join(' · '); })();
  var jurisText = fig.jurisdictions && fig.jurisdictions.length ? fig.jurisdictions.join(' · ') : '';
  var title = stateName + ' · ' + (single ? single.period.label : '');
  var retSuffix = multi && ret ? ret.label : '';
  var subtitle = [multi && ret && ret.rate ? ratePct(ret.rate) + ' of taxable' : '', jurisText, projectsText, deliveryBasis ? 'delivery basis' : '', dueDate ? 'return due ' + salesTaxShortDate(dueDate, true) : ''].filter(Boolean).join(' · ');

  // ── Actions.
  function toggleItem(id) { setTicked(function(prev) { return Object.assign({}, prev, { [id]: !prev[id] }); }); }
  function addFiles(list) {
    var arr = Array.prototype.slice.call(list || []).filter(function(f) { return f && (f.type === 'application/pdf' || /^image\//.test(f.type) || /\.pdf$/i.test(f.name)); });
    if (!arr.length) return;
    setPendingFiles(function(prev) { return prev.concat(arr.map(function(f) { return { localId: mintId(), file: f }; })); });
  }
  function copyFigures() {
    if (single) copyText(salesTaxFilingText(single.period, single.st), function() { flash('figures'); });
  }
  function csvDetail() {
    if (!single) return;
    var view = { periods: [Object.assign({}, single.period, { states: [single.st] })], pending: { count: 0, tax: 0, byState: [] }, exports: [], unassigned: [], grandTax: fig.tax };
    downloadCsv('sales-tax-' + stateCode + '-' + single.period.key + '.csv', salesTaxReportCsv(view, salesTaxFilings));
  }

  async function save() {
    if (busy || !single) return;
    var amt = r2(draft.amountPaid);
    if (isNaN(amt)) { setErr('Enter the amount paid.'); return; }
    if (!ctx.commitSalesTaxFilings) { setErr('Filing recorder not available.'); return; }
    setBusy(true); setErr('');
    // The return is chosen before the modal opens (2026-10-16): its key
    // and jurisdiction ride the filing; nothing is typed.
    var shared = { paidDate: draft.paidDate, filedDate: draft.filedDate, method: draft.method, confirmationNo: draft.confirmationNo, notes: draft.notes, jurisdiction: ret ? ret.jurisdiction : '', returnKey: ret ? ret.key : '', recordedBy: existing ? existing.recordedBy : '' };
    var entries = [{
      // taxDueSnapshot is "what the report said when this was FILED". An
      // edit keeps it (handoff v3 §8.6, audit finding #3) — only a new
      // filing takes today's figure.
      filing: Object.assign({ id: idRef.current || (idRef.current = existing ? existing.id : mintId()), stateCode: stateCode, periodKey: single.period.key, grouping: grouping, taxDueSnapshot: existing && existing.taxDueSnapshot != null ? existing.taxDueSnapshot : retTax, amountPaid: amt }, shared),
      // Stored facts stay stored: an item already stamped by THIS filing keeps
      // its frozen tax_amount; only a newly ticked item takes the row figure.
      // Stamps for items no longer on the report row (deleted / moved) are
      // kept untouched — editing a confirmation number never unstamps.
      items: tickedItems.map(function(it) {
        var prior = existingStamps.find(function(fi) { return fi.itemId === it.itemId; });
        return { itemId: it.itemId, invoiceRef: it.invoiceRef, taxAmount: prior ? prior.taxAmount : it.tax };
      }).concat(existingStamps.filter(function(fi) { return !allItems.some(function(it) { return it.itemId === fi.itemId; }); })
        .map(function(fi) { return { itemId: fi.itemId, invoiceRef: fi.invoiceRef, taxAmount: fi.taxAmount }; })),
    }];
    var res = await ctx.commitSalesTaxFilings({ filings: entries, newFiles: pendingFiles, removeFileIds: removedFileIds });
    if (res && res.uploaded && res.uploaded.length) setPendingFiles(function(prev) { return prev.filter(function(p) { return res.uploaded.indexOf(p.localId) < 0; }); });
    if (!res || !res.ok) { setErr((res && res.error) || 'Could not save the filing.'); setBusy(false); return; }
    setBusy(false);
    if (props.onClose) props.onClose({ saved: true, filingIds: res.filingIds });
  }
  async function remove() {
    if (!existing || !ctx.deleteSalesTaxFiling) return;
    if (!window.confirm('Remove this filing? Its item stamps and attached files go with it.')) return;
    setBusy(true); setErr('');
    var ok = await ctx.deleteSalesTaxFiling(existing.id);
    setBusy(false);
    if (!ok) { setErr('The filing could not be removed — it is still recorded. Try again, or reload.'); return; }
    if (props.onClose) props.onClose({ deleted: true });
  }
  async function openFile(f) {
    if (!ctx.salesTaxFilingFileUrl) return;
    var url = await ctx.salesTaxFilingFileUrl(f.storagePath);
    if (url) window.open(url, '_blank', 'noopener');
  }

  // ── Stepper.
  function stepCell(n, label) {
    var cur = step === n, done = step > n;
    return (
      <div key={n} onClick={function() { setStep(n); }}
           style={{ flex: 1, paddingBottom: 10, cursor: 'pointer', borderBottom: cur ? '2px solid ' + INK_1 : done ? '2px solid ' + ZONE_GREEN : '0.5px solid ' + LINE_CONTAINER }}>
        <div style={Object.assign({}, EYEBROW, { color: cur ? INK_1 : done ? ZONE_GREEN : INK_3 })}>{label}</div>
      </div>
    );
  }
  var nextLabel = step === 1 ? 'Continue → File with ' + stateName : step === 2 ? "I've filed — record it →" : 'Save filing';
  function next() { if (step < 3) setStep(step + 1); else save(); }

  // ── Step 1.
  function figuresColumn() {
    var ded = deductionRows(fig);
    return (
      <div>
        <div style={Object.assign({}, EYEBROW, { marginBottom: 8 })}>Figures for the return</div>
        <div style={FIG_ROW}><span style={{ color: INK_2 }}>Gross sales</span><span style={{ fontFeatureSettings: TABULAR }}>{fmtMoney(fig.grossSales)}</span></div>
        <div style={FIG_ROW}>
          <span style={{ color: INK_2 }}>Less non-taxable {ded.length > 0 && <span style={{ color: INK_3, fontSize: 10 }}>{ded.map(function(d) { return d.label; }).join(', ')}</span>}</span>
          <span style={{ fontFeatureSettings: TABULAR }}>− {fmtMoney(fig.nonTaxable)}</span>
        </div>
        <div style={FIG_ROW}><span style={{ color: INK_2 }}>Taxable sales</span><span style={{ fontFeatureSettings: TABULAR }}>{fmtMoney(fig.taxable)}</span></div>
        <div style={FIG_ROW}>
          <span style={{ color: INK_2 }}>Rate {multi && ret && ret.authorities && ret.authorities.length
            ? <span style={{ color: INK_3, fontSize: 10 }} data-testid="filing-return-authorities">{ret.authorities.map(function(a) { return a.name + ' ' + ratePct(a.rate); }).join(' + ')}</span>
            : (jurisText && <span style={{ color: INK_3, fontSize: 10 }}>{stateCode} · {jurisText}</span>)}</span>
          <span style={{ fontFeatureSettings: TABULAR }}>{ratePct(fig.rate)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 13, fontWeight: 500 }}><span>Tax due</span><span style={{ color: ZONE_FUCHSIA, fontFeatureSettings: TABULAR }}>{fmtMoney(fig.tax)}</span></div>
        <div style={{ fontSize: 10, color: INK_3, lineHeight: 1.5, marginTop: 6 }} data-testid="filing-step1-note">
          {fig.taxedCategories && fig.taxedCategories.length > 0 ? 'Taxed here: ' + catLabels(fig.taxedCategories) + '. ' : ''}
          {deliveryBasis
            ? <span>{stateName} is on <span style={{ color: INK_1 }}>delivery basis</span>: items fall in the month they were delivered into the state, not the month the client paid. Figures are frozen from each final invoice&rsquo;s tax snapshot.</span>
            : <span>Figures are frozen from each final invoice&rsquo;s tax snapshot at the time the client paid.</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
          <button onClick={copyFigures} style={GHOST26}>{copied === 'figures' ? 'Copied' : 'Copy figures'}</button>
          <button onClick={csvDetail} style={GHOST26}>↓ CSV detail</button>
        </div>
      </div>
    );
  }
  // Step 1 rows read the SHARE rows (allItems), never the raw group item —
  // on a multi-return state the figure beside each item is this return's.
  var shareById = {};
  allItems.forEach(function(it) { shareById[it.itemId] = it; });
  function itemRow(raw) {
    var it = shareById[raw.itemId] || raw;
    var on = !!ticked[it.itemId];
    var elsewhere = stampedElsewhere[it.itemId];
    var color = on ? INK_1 : INK_4;
    return (
      <label key={it.itemId} style={{ display: 'grid', gridTemplateColumns: '14px 1fr auto', gap: 8, alignItems: 'baseline', padding: '4px 0', borderBottom: HAIRLINE_ROW, fontSize: 11, cursor: 'pointer' }}>
        <input type="checkbox" checked={on} onChange={function() { toggleItem(it.itemId); }} style={{ margin: 0, accentColor: INK_1, width: 12, height: 12 }} data-testid={'filing-item-' + it.itemId} />
        <span style={{ color: color }}>
          <span style={{ color: on ? INK_3 : INK_4, marginRight: 6 }}>{it.sku}</span>{it.name}
          {elsewhere && <span style={{ color: INK_3, marginLeft: 6, fontSize: 10 }} title="Already stamped by another filing">· stamped ({elsewhere})</span>}
          {it.source === 'live' && <span style={{ color: INK_3, marginLeft: 6, fontSize: 10 }} title="No frozen snapshot for this final — the figure reads the live item.">live</span>}
        </span>
        <span style={{ fontFeatureSettings: TABULAR, color: color }}>{fmtMoney(it.tax)}{multi && <span style={{ color: INK_3, fontSize: 10, marginLeft: 6 }} data-testid={'filing-item-share-' + it.itemId}>{'of ' + fmtMoney(it.itemTotal)}</span>}</span>
      </label>
    );
  }
  function step1() {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {figuresColumn()}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <div style={EYEBROW}>Items behind this figure · {tickedItems.length} of {allItems.length}</div>
            <div style={{ fontSize: 10, color: INK_3 }}>untick to leave an item out</div>
          </div>
          {groups.map(function(g) {
            return (
              <div key={(g.invoiceId || g.invoiceRef) + ':' + (g.bucketDate || '')}>
                <div style={{ fontSize: 10, color: INK_3, padding: '6px 0 3px' }}>
                  {g.invoiceRef} <span style={{ marginLeft: 6 }}>{g.paidBasis === 'closed' ? 'closed' : 'paid'} {salesTaxShortDate(g.paidDate, true)}</span>
                  {g.deliveryBasis && g.bucketDate && <span style={{ marginLeft: 6 }}>· delivered {salesTaxShortDate(g.bucketDate, true)}</span>}
                  {g.partial && <span style={{ marginLeft: 6 }} title="The rest of this final invoice's items were delivered in another period and file there.">· part of this invoice</span>}
                </div>
                {g.items.map(itemRow)}
              </div>
            );
          })}
          {allItems.length === 0 && <div style={{ fontSize: 11, color: INK_3 }}>No items on this row.</div>}
        </div>
      </div>
    );
  }

  // ── Step 2.
  function fieldRow(label, value, mono, extra) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr auto', gap: 8, alignItems: 'center', marginTop: 4, fontSize: 12 }}>
        <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase', color: INK_3 }}>{label}</span>
        <span style={mono ? { fontFamily: 'monospace' } : undefined}>{value}</span>
        <div style={{ display: 'flex', gap: 4 }}>{extra}</div>
      </div>
    );
  }
  // Line-by-line guide for the known forms (DR 0100, Town of Vail);
  // the generic four lines elsewhere. Values copy unformatted.
  var formLines = salesTaxReturnFormLines(stateCode, ret ? ret.key : '', fig, ret);
  var formName = stateCode === 'CO' ? (ret && ret.key === 'VAIL' ? 'Town of Vail sales tax return' : (!ret || !ret.key ? 'DR 0100' : ''))
    : stateCode === 'WY' && (!ret || !ret.key) ? 'WYIFS Form 41' : '';
  function formLineRow(row, key) {
    var strong = !!row.strong;
    return (
      <div style={Object.assign({}, FIG_ROW, { alignItems: 'flex-start' }, strong ? { fontWeight: 500 } : {})}>
        <span style={{ color: strong ? INK_1 : INK_2, display: 'flex', gap: 8, minWidth: 0 }}>
          {row.line ? <span style={{ fontFeatureSettings: TABULAR, color: INK_3, fontSize: 10, minWidth: 28, paddingTop: 2 }}>{row.line}</span> : <span style={{ minWidth: 28 }} />}
          <span style={{ minWidth: 0 }}>{row.label}{row.note ? <div style={{ fontSize: 10, color: INK_3, fontWeight: 400 }}>{row.note}</div> : null}</span>
        </span>
        {row.text != null
          ? <span style={{ color: INK_2, fontSize: 11, textAlign: 'right', maxWidth: 180 }}>{row.text}</span>
          : <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontFeatureSettings: TABULAR, color: strong ? ZONE_FUCHSIA : INK_1 }}>{plain(row.value)}</span>
              <button onClick={function() { copyText(plain(row.value), function() { flash(key); }); }} style={COPY_BTN}>{copied === key ? 'Copied' : 'Copy'}</button>
            </span>}
      </div>
    );
  }
  function step2() {
    var canSeeCreds = isOwner && stateRec;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div>
          <div style={Object.assign({}, EYEBROW, { marginBottom: 8 })}>State portal</div>
          <div style={{ border: HAIRLINE, borderRadius: 6, padding: '14px 16px' }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{multi && ret && ret.key ? ret.label + ' portal' : stateName + ' tax portal'}</div>
            <div style={{ fontSize: 11, color: INK_3, marginTop: 2 }}>{[stateRec && stateRec.certFormLabel, salesTaxDueRuleLabel(dueRule)].filter(Boolean).join(' · ') || 'Sales & use tax return'}</div>
            {canSeeCreds && (
              <div style={{ marginTop: 12 }}>
                {fieldRow('Reg ID', stateRec.registrationId || '—', false, stateRec.registrationId ? <button onClick={function() { copyText(stateRec.registrationId, function() { flash('reg'); }); }} style={COPY_BTN}>{copied === 'reg' ? 'Copied' : 'Copy'}</button> : null)}
                {fieldRow('Username', stateRec.username || '—', false, stateRec.username ? <button onClick={function() { copyText(stateRec.username, function() { flash('user'); }); }} style={COPY_BTN}>{copied === 'user' ? 'Copied' : 'Copy'}</button> : null)}
                {fieldRow('Password', stateRec.password ? (reveal ? stateRec.password : '••••••••') : '—', true, stateRec.password ? [
                  <button key="s" onClick={function() { setReveal(!reveal); }} style={COPY_BTN}>{reveal ? 'Hide' : 'Show'}</button>,
                  <button key="c" onClick={function() { copyText(stateRec.password, function() { flash('pw'); }); }} style={COPY_BTN}>{copied === 'pw' ? 'Copied' : 'Copy'}</button>,
                ] : null)}
                {stateRec.pin ? fieldRow('PIN', reveal ? stateRec.pin : '••••', true, [
                  <button key="s" onClick={function() { setReveal(!reveal); }} style={COPY_BTN}>{reveal ? 'Hide' : 'Show'}</button>,
                  <button key="c" onClick={function() { copyText(stateRec.pin, function() { flash('pin'); }); }} style={COPY_BTN}>{copied === 'pin' ? 'Copied' : 'Copy'}</button>,
                ]) : null}
              </div>
            )}
            <div style={{ fontSize: 10, color: INK_3, marginTop: 8 }}>Credentials and the portal link show for the owner only.</div>
            {canSeeCreds && stateRec.portalUrl && (
              <a href={stateRec.portalUrl} target="_blank" rel="noopener noreferrer"
                 style={{ display: 'inline-flex', alignItems: 'center', height: 32, padding: '0 16px', borderRadius: 18, background: INK_1, color: '#FFFFFF', fontSize: 12, fontWeight: 500, textDecoration: 'none', marginTop: 14 }}>
                Open portal ↗
              </a>
            )}
          </div>
        </div>
        <div>
          <div style={Object.assign({}, EYEBROW, { marginBottom: 8 })}>Enter on the return{formName ? <span style={{ textTransform: 'none', letterSpacing: 0, marginLeft: 6 }}>· {formName}</span> : null}</div>
          <div data-testid="filing-form-lines">
            {formLines.map(function(row, i) { return <div key={i}>{formLineRow(row, 'fl' + i)}</div>; })}
          </div>
          <div style={{ fontSize: 11, color: INK_2, lineHeight: 1.6, marginTop: 14 }} data-testid="filing-form-note">
            {formName
              ? 'Gross goes on line 1 and the untaxed categories come off as a deduction, so this return, the ' + (multi ? 'other return' : 'state') + ' and the client invoice all show the same gross and tax the same base. '
              : ''}
            File and pay on the state&rsquo;s site, then come back here. When you&rsquo;re done, save the confirmation PDF from the portal &mdash; you&rsquo;ll attach it in the next step.
          </div>
          <div style={{ fontSize: 11, color: INK_3, lineHeight: 1.6, marginTop: 6 }}>If the portal&rsquo;s tax differs by a cent or two from Gilda&rsquo;s, record what the state charged &mdash; the difference prints beside the row.</div>
        </div>
      </div>
    );
  }

  // ── Step 3.
  function fileCard(name, meta, onRemove, onOpen) {
    return (
      <div style={{ border: HAIRLINE, borderRadius: 6, padding: '10px 12px', display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: 10, alignItems: 'center', marginBottom: 8 }}>
        <div style={{ width: 28, height: 34, border: '0.5px solid ' + LINE_SHELL, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 500, color: INK_3, letterSpacing: '0.05em' }}>PDF</div>
        <div>
          <div style={{ fontSize: 12, color: INK_1, cursor: onOpen ? 'pointer' : 'default', textDecoration: onOpen ? 'underline' : 'none', textUnderlineOffset: 3 }} onClick={onOpen || undefined}>{name}</div>
          <div style={{ fontSize: 10, color: INK_3, marginTop: 2 }}>{meta}</div>
        </div>
        <button onClick={onRemove} style={{ border: 'none', background: 'transparent', color: INK_3, fontSize: 16, cursor: 'pointer', lineHeight: 1 }} title="Remove">×</button>
      </div>
    );
  }
  function filesBlock() {
    var any = pendingFiles.length > 0 || existingFiles.length > 0;
    var dropProps = {
      onDragOver: function(e) { e.preventDefault(); setDragOver(true); },
      onDragLeave: function() { setDragOver(false); },
      onDrop: function(e) { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer && e.dataTransfer.files); },
      onClick: function() { if (fileInputRef.current) fileInputRef.current.click(); },
    };
    return (
      <div>
        <input ref={fileInputRef} type="file" accept="application/pdf,image/*" multiple style={{ display: 'none' }} onChange={function(e) { addFiles(e.target.files); e.target.value = ''; }} data-testid="filing-file-input" />
        {!any ? (
          <div {...dropProps} style={{ border: DASHED_DROP, borderColor: dragOver ? INK_1 : undefined, borderRadius: 6, padding: '28px 16px', textAlign: 'center', cursor: 'pointer' }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: INK_1 }}>Drop the confirmation PDF here <span style={{ color: INK_3, fontWeight: 400, margin: '0 5px' }}>or</span><span style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}>click to choose</span></div>
            <div style={{ fontSize: 10, color: INK_3, marginTop: 6 }}>The return and payment receipt from the portal. Several files allowed.</div>
          </div>
        ) : (
          <div>
            {existingFiles.map(function(f) {
              return <div key={f.id}>{fileCard(f.filename || 'file', [fmtBytes(f.sizeBytes), 'uploaded by ' + salesTaxRecorderName(f.uploadedBy, ctx.firmSettings), salesTaxShortDate(f.uploadedAt, true)].filter(Boolean).join(' · '), function() { setRemovedFileIds(function(prev) { return prev.concat([f.id]); }); }, function() { openFile(f); })}</div>;
            })}
            {pendingFiles.map(function(p) {
              return <div key={p.localId}>{fileCard(p.file.name, fmtBytes(p.file.size) + ' · uploaded by ' + recorderName + ', just now', function() { setPendingFiles(function(prev) { return prev.filter(function(x) { return x.localId !== p.localId; }); }); }, null)}</div>;
            })}
            <div {...dropProps} style={{ border: DASHED_DROP, borderColor: dragOver ? INK_1 : undefined, borderRadius: 6, padding: '10px 16px', textAlign: 'center', fontSize: 11, color: INK_3, cursor: 'pointer' }}>+ add another file (payment receipt)</div>
          </div>
        )}
      </div>
    );
  }
  function onSaveBlock() {
    return (
      <div style={{ marginTop: 18, borderTop: HAIRLINE_ROW, paddingTop: 12 }}>
        <div style={Object.assign({}, EYEBROW, { marginBottom: 6 })}>On save</div>
        <div style={{ fontSize: 11, color: INK_2, lineHeight: 1.6 }}>
          Writes the filing against <span style={{ color: INK_1 }}>{stateCode} · {single ? single.period.label : ''}{multi && ret ? ' · ' + ret.label : ''}</span> and stamps <span style={{ color: INK_1 }}>{tickedItems.length} item{tickedItems.length === 1 ? '' : 's'} on {finCount} final invoice{finCount === 1 ? '' : 's'}</span> as tax remitted &mdash; paid date, method, confirmation and the PDF ride on each item. Editing or deleting the filing updates the items.
        </div>
        {untickedItems.length > 0 && <div style={{ fontSize: 11, color: ALERT_AMBER_TEXT, lineHeight: 1.6, marginTop: 6 }}>{untickedItems.length} item(s) left out &mdash; their tax ({fmtMoney(untickedTax)}) stays open on this row.</div>}
      </div>
    );
  }
  function paymentForm() {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={{ gridColumn: '1 / -1' }}><span style={LABEL}>Amount paid to {multi && ret && ret.key ? ret.label : stateName}</span>
          <CurrencyInput value={draft.amountPaid} onValueChange={function(n) { setD('amountPaid', n); }} style={AMOUNT} data-testid="filing-amount" /></label>
        <label><span style={LABEL}>Paid date</span><input type="date" value={draft.paidDate || ''} onChange={function(e) { setD('paidDate', e.target.value); }} style={INPUT} data-testid="filing-paid-date" /></label>
        <label><span style={LABEL}>Filed date</span><input type="date" value={draft.filedDate || ''} onChange={function(e) { setD('filedDate', e.target.value); }} style={INPUT} /></label>
        <div style={{ gridColumn: '1 / -1' }}><span style={LABEL}>Method</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {FILING_METHODS.map(function(m) { return <button key={m} type="button" onClick={function() { setD('method', m); }} style={methodPill(draft.method === m)} data-testid={'filing-method-' + m}>{m}</button>; })}
          </div>
        </div>
        <label><span style={LABEL}>Confirmation #</span><input value={draft.confirmationNo} onChange={function(e) { setD('confirmationNo', e.target.value); }} style={INPUT} placeholder="from the portal" data-testid="filing-confirmation" /></label>
        <div><span style={LABEL}>Return</span><div style={{ fontSize: 12, color: INK_1, padding: '7px 0' }} data-testid="filing-return">{ret ? ret.label : stateName + ' state return'}</div></div>
        <label style={{ gridColumn: '1 / -1' }}><span style={LABEL}>Notes</span><input value={draft.notes} onChange={function(e) { setD('notes', e.target.value); }} style={INPUT} placeholder="optional" /></label>
      </div>
    );
  }
  function step3() {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div>
          <div style={Object.assign({}, EYEBROW, { marginBottom: 8 })}>Payment</div>
          {paymentForm()}
        </div>
        <div>
          <div style={Object.assign({}, EYEBROW, { marginBottom: 8 })}>Filed return (PDF)</div>
          {filesBlock()}
          {onSaveBlock()}
        </div>
      </div>
    );
  }

  var saveDisabled = busy;
  return (
    <div style={{ position: 'fixed', inset: 0, background: SCRIM, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid="sales-tax-filing-modal">
      <div style={{ width: 720, maxWidth: '96vw', maxHeight: '92vh', background: SURFACE, borderRadius: 6, boxShadow: SHADOW_MODAL, display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '16px 20px', borderBottom: HAIRLINE_SHELL }}>
          <div>
            <div style={{ fontSize: 14, color: INK_1, fontWeight: 500 }} data-testid="filing-title">
              {title}
              {retSuffix && <span style={{ color: INK_3, fontWeight: 400, marginLeft: 6 }} data-testid="filing-return-suffix">{retSuffix}</span>}
              {firstReturn && <span style={{ color: INK_3, fontWeight: 400, marginLeft: 6 }} data-testid="filing-first-return">first return</span>}
            </div>
            <div style={{ fontSize: 11, color: INK_3, marginTop: 2 }} data-testid="filing-subtitle">{subtitle}</div>
          </div>
          <button onClick={function() { if (props.onClose) props.onClose({}); }} style={{ border: 'none', background: 'transparent', color: INK_3, fontSize: 18, lineHeight: 1, cursor: 'pointer' }} title="Close">×</button>
        </div>
        <div style={{ display: 'flex', padding: '12px 20px 0' }}>
          {stepCell(1, "Step 1 · What's due")}
          {stepCell(2, 'Step 2 · File with the state')}
          {stepCell(3, 'Step 3 · Record the filing')}
        </div>
        <div style={{ padding: '18px 20px', overflowY: 'auto', flex: 1 }}>
          {step === 1 && step1()}
          {step === 2 && step2()}
          {step === 3 && step3()}
          {err && <div style={{ marginTop: 14, fontSize: 11, color: ALERT_RED_TEXT, lineHeight: 1.6 }} data-testid="filing-error">{err}</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: HAIRLINE_SHELL }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={function() { setStep(Math.max(1, step - 1)); }} style={Object.assign({}, BACK_BTN, { visibility: step > 1 ? 'visible' : 'hidden' })}>← Back</button>
            {existing && step === 3 && <button onClick={remove} disabled={busy} style={Object.assign({}, BACK_BTN, { color: ALERT_RED_TEXT })}>Delete</button>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {step === 3 && <span style={{ fontSize: 11, color: INK_3 }}>Recorded by {recorderName} · {salesTaxShortDate(existing ? (existing.createdAt || todayIso()) : todayIso(), true)}</span>}
            <button onClick={next} disabled={saveDisabled} style={Object.assign({}, PRIMARY, saveDisabled ? { opacity: 0.5, cursor: 'default' } : {})} data-testid="filing-next">{busy ? 'Saving…' : nextLabel}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
